import {
  Alert,
  Badge,
  Button,
  FileInput,
  Group,
  Progress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconFileSpreadsheet } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { ConfirmActionModal, LoadingState, PageHeader, StatCard } from "@how/ui";
import { CrmTabs } from "../CrmTabs";
import {
  IMPORT_FIELDS,
  IMPORT_LIMITS,
  guessMapping,
  parseCsv,
  toImportRows,
  type ImportField,
} from "./csv";
import {
  useCommitImport,
  useDeleteImport,
  useImport,
  useImportProblems,
  useImports,
  useStageImport,
} from "./import-queries";
import { PlanningCenterImport } from "./PlanningCenterImport";

interface ParsedFile {
  readonly name: string;
  readonly lines: string[][];
}

const STATUS_LABEL: Record<string, string> = {
  staging: "Checking",
  validated: "Ready to import",
  committing: "Importing",
  completed: "Finished",
  cancelled: "Cancelled",
};

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/**
 * Importing a membership list, in the order a church works through it: choose the file, say which column is which,
 * read the report, then commit. Nothing about the church's people changes before the last step, and once committed
 * the work happens on the server, so the tab can be closed and the import reopened from the list below.
 */
export function ImportPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<ParsedFile | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Partial<Record<ImportField, number>>>({});
  const [staged, setStaged] = useState(0);
  const [openImport, setOpenImport] = useState<string | null>(null);
  const [source, setSource] = useState<"file" | "planning_center">("file");

  const stage = useStageImport();
  const imports = useImports();

  const headers = file?.lines[0] ?? [];
  const dataRows = file === null ? 0 : file.lines.length - 1;
  const missingRequired = IMPORT_FIELDS.filter((f) => f.required && mapping[f.key] === undefined);

  const choose = async (chosen: File | null) => {
    setFile(null);
    setFileError(null);
    setOpenImport(null);
    if (chosen === null) return;
    if (!chosen.name.toLowerCase().endsWith(".csv")) {
      setFileError(
        "Choose a CSV file. From a spreadsheet, use File, then Save As or Download, then CSV.",
      );
      return;
    }
    if (chosen.size > IMPORT_LIMITS.maxBytes) {
      setFileError(
        `This file is ${megabytes(chosen.size)}. The limit is ${megabytes(IMPORT_LIMITS.maxBytes)}.`,
      );
      return;
    }
    const lines = parseCsv(await chosen.text());
    if (lines.length < 2) {
      setFileError("The file has a header row and nothing else, or nothing at all.");
      return;
    }
    if (lines.length - 1 > IMPORT_LIMITS.maxRows) {
      setFileError(
        `This file has ${(lines.length - 1).toLocaleString()} rows. The limit is ${IMPORT_LIMITS.maxRows.toLocaleString()}; split it and import each part.`,
      );
      return;
    }
    setFile({ name: chosen.name, lines });
    setMapping(guessMapping(lines[0] ?? []));
  };

  const preview = useMemo(
    () => (file === null ? [] : toImportRows(file.lines.slice(0, 4), mapping)),
    [file, mapping],
  );

  return (
    <>
      <PageHeader
        title="People"
        description="Everyone your church knows, whether or not they have a login."
      />
      <CrmTabs active="people" />

      <Stack gap="xl">
        {openImport === null ? (
          <SegmentedControl
            aria-label="Where the people come from"
            value={source}
            onChange={(value) => {
              setSource(value === "planning_center" ? "planning_center" : "file");
              setFile(null);
              setFileError(null);
            }}
            data={[
              { value: "file", label: "From a file" },
              { value: "planning_center", label: "From Planning Center" },
            ]}
            w="fit-content"
          />
        ) : null}
        {openImport === null && source === "planning_center" ? (
          <PlanningCenterImport onStaged={setOpenImport} />
        ) : null}
        {openImport === null && source === "file" ? (
          <Stack gap="md">
            <Title order={3}>Import people from a file</Title>
            <Text size="sm" c="dimmed" maw={640}>
              A CSV file with a header row, up to {megabytes(IMPORT_LIMITS.maxBytes)} and{" "}
              {IMPORT_LIMITS.maxRows.toLocaleString()} rows. You will see a report of what would be
              imported before anything changes.
            </Text>
            <FileInput
              label="File"
              placeholder="Choose a CSV file"
              accept=".csv,text/csv"
              leftSection={<IconFileSpreadsheet size={16} />}
              clearable
              maw={420}
              onChange={(chosen) => void choose(chosen)}
            />
            {fileError === null ? null : (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                {fileError}
              </Alert>
            )}

            {file === null ? null : (
              <Stack gap="md">
                <Text size="sm">
                  <strong>{file.name}</strong>: {dataRows.toLocaleString()} rows, {headers.length}{" "}
                  columns. Check which column holds each field; most are matched already.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
                  {IMPORT_FIELDS.map((field) => (
                    <Select
                      key={field.key}
                      label={field.label}
                      required={field.required}
                      placeholder="Not in this file"
                      clearable={!field.required}
                      data={headers.map((header, index) => ({
                        value: String(index),
                        label: header.length === 0 ? `Column ${String(index + 1)}` : header,
                      }))}
                      value={mapping[field.key] === undefined ? null : String(mapping[field.key])}
                      onChange={(value) => {
                        // Rebuilt rather than mutated with delete, so a cleared field leaves no key behind.
                        setMapping((current) => ({
                          ...Object.fromEntries(
                            Object.entries(current).filter(([key]) => key !== field.key),
                          ),
                          ...(value === null ? {} : { [field.key]: Number(value) }),
                        }));
                      }}
                    />
                  ))}
                </SimpleGrid>

                {preview.length === 0 ? null : (
                  <div className="how-table-frame" style={{ overflowX: "auto" }}>
                    <Table
                      verticalSpacing={7}
                      className="how-table"
                      aria-label="How the first rows will be read"
                    >
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Row</Table.Th>
                          {IMPORT_FIELDS.filter((f) => mapping[f.key] !== undefined).map((f) => (
                            <Table.Th key={f.key}>{f.label}</Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {preview.map((row) => (
                          <Table.Tr key={row.row_number}>
                            <Table.Td className="how-numeric">{row.row_number}</Table.Td>
                            {IMPORT_FIELDS.filter((f) => mapping[f.key] !== undefined).map((f) => (
                              <Table.Td key={f.key}>{String(row[f.key] ?? "")}</Table.Td>
                            ))}
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </div>
                )}

                {stage.isPending ? (
                  <Stack gap={4} maw={420}>
                    <Text size="sm">
                      Checking {staged.toLocaleString()} of {dataRows.toLocaleString()} rows
                    </Text>
                    <Progress
                      value={dataRows === 0 ? 0 : (staged / dataRows) * 100}
                      aria-label="Rows checked"
                    />
                  </Stack>
                ) : null}

                <Group>
                  <Button
                    disabled={missingRequired.length > 0}
                    loading={stage.isPending}
                    onClick={() => {
                      setStaged(0);
                      stage.mutate(
                        {
                          fileName: file.name,
                          rows: toImportRows(file.lines, mapping),
                          onProgress: setStaged,
                        },
                        {
                          onSuccess: (importId) => {
                            setOpenImport(importId);
                            setFile(null);
                          },
                        },
                      );
                    }}
                  >
                    Check {dataRows.toLocaleString()} rows
                  </Button>
                  {missingRequired.length === 0 ? null : (
                    <Text size="sm" c="dimmed">
                      Choose the {missingRequired.map((f) => f.label.toLowerCase()).join(" and ")}{" "}
                      column first.
                    </Text>
                  )}
                </Group>
                {stage.isError ? (
                  <Alert color="red" variant="light">
                    The file could not be checked. {stage.error.message}
                  </Alert>
                ) : null}
              </Stack>
            )}
          </Stack>
        ) : null}
        {openImport === null ? null : (
          <ImportReport
            importId={openImport}
            onClose={() => {
              setOpenImport(null);
            }}
            onViewPeople={() => {
              void navigate("/people");
            }}
          />
        )}

        <Stack gap="xs">
          <Title order={4}>Recent imports</Title>
          {(imports.data ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">
              None yet.
            </Text>
          ) : (
            <div className="how-table-frame" style={{ overflowX: "auto" }}>
              <Table
                verticalSpacing={9}
                className="how-table"
                highlightOnHover
                aria-label="Recent imports"
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>File</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th className="how-numeric">Rows</Table.Th>
                    <Table.Th className="how-numeric">Imported</Table.Th>
                    <Table.Th className="how-numeric">Started</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(imports.data ?? []).map((record) => (
                    <Table.Tr
                      key={record.id}
                      style={{ cursor: "pointer" }}
                      tabIndex={0}
                      onClick={() => {
                        setOpenImport(record.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") setOpenImport(record.id);
                      }}
                    >
                      <Table.Td>{record.file_name}</Table.Td>
                      <Table.Td>
                        <Badge
                          variant="light"
                          color={record.status === "completed" ? "teal" : "gray"}
                        >
                          {STATUS_LABEL[record.status] ?? record.status}
                        </Badge>
                      </Table.Td>
                      <Table.Td className="how-numeric">
                        {record.row_count.toLocaleString()}
                      </Table.Td>
                      <Table.Td className="how-numeric">
                        {record.imported_count.toLocaleString()}
                      </Table.Td>
                      <Table.Td className="how-numeric">
                        {new Date(record.created_at).toLocaleDateString()}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          )}
        </Stack>
      </Stack>
    </>
  );
}

/** The dry-run report, the commit, and progress while the server works through it. */
function ImportReport({
  importId,
  onClose,
  onViewPeople,
}: {
  readonly importId: string;
  readonly onClose: () => void;
  readonly onViewPeople: () => void;
}) {
  const record = useImport(importId);
  const problems = useImportProblems(importId);
  const commit = useCommitImport();
  const remove = useDeleteImport();
  const [confirming, setConfirming] = useState(false);

  const data = record.data;
  if (data === undefined) return <LoadingState rows={3} />;
  const finished = data.status === "completed";

  const progress = data.valid_count === 0 ? 0 : (data.imported_count / data.valid_count) * 100;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>{data.file_name}</Title>
        <Button variant="subtle" onClick={onClose}>
          Import another file
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <StatCard
          label={finished ? "Imported" : "Ready to import"}
          value={(finished ? data.imported_count : data.valid_count).toLocaleString()}
          role="success"
          hint={finished ? "Added to your people." : "New people, not yet added."}
        />
        <StatCard
          label="Already in your church"
          value={data.duplicate_count.toLocaleString()}
          role={data.duplicate_count > 0 ? "warning" : "neutral"}
          hint="Matched on email, or on name and birthdate. Skipped."
        />
        <StatCard
          label="Need fixing"
          value={data.invalid_count.toLocaleString()}
          role={data.invalid_count > 0 ? "danger" : "neutral"}
          hint="Skipped. Correct them in the file and import them again."
        />
      </SimpleGrid>

      {data.status === "validated" ? (
        <Group>
          <Button
            disabled={data.valid_count === 0}
            loading={commit.isPending}
            onClick={() => {
              commit.mutate(importId);
            }}
          >
            Import {data.valid_count.toLocaleString()} people
          </Button>
          <Button
            variant="subtle"
            color="red"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Cancel this import
          </Button>
        </Group>
      ) : null}

      {data.status === "committing" ? (
        <Stack gap={4} maw={480}>
          <Text size="sm">
            Imported {data.imported_count.toLocaleString()} of {data.valid_count.toLocaleString()}.
            This carries on if you leave the page.
          </Text>
          <Progress value={progress} animated aria-label="Import progress" />
        </Stack>
      ) : null}

      {data.status === "completed" ? (
        <Group>
          <Button onClick={onViewPeople}>View people</Button>
          <Button
            variant="subtle"
            onClick={() => {
              setConfirming(true);
            }}
          >
            Discard the staged rows
          </Button>
        </Group>
      ) : null}

      {(problems.data ?? []).length === 0 ? null : (
        <Stack gap="xs">
          <Title order={4}>
            {finished ? "Rows that were not imported" : "Rows that will not be imported"}
          </Title>
          <div className="how-table-frame" style={{ overflowX: "auto" }}>
            <Table
              verticalSpacing={7}
              className="how-table"
              aria-label="Rows that will not be imported"
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className="how-numeric">Row</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Why</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(problems.data ?? []).map((row) => (
                  <Table.Tr key={row.row_number}>
                    <Table.Td className="how-numeric">{row.row_number}</Table.Td>
                    <Table.Td>
                      {[row.first_name, row.last_name].filter((part) => part !== null).join(" ") ||
                        "No name"}
                    </Table.Td>
                    <Table.Td>
                      {row.status === "duplicate"
                        ? `Already in your church${row.email === null ? "" : ` (${row.email})`}`
                        : row.errors.join("; ")}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        </Stack>
      )}

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={data.status === "completed" ? "Discard staged rows" : "Cancel import"}
        message={
          data.status === "completed"
            ? `Discard the ${data.row_count.toLocaleString()} staged rows from ${data.file_name}? The people already imported stay; only the working copy goes.`
            : `Cancel the import of ${data.file_name}? Nothing has been added to your people yet, and the staged rows are deleted.`
        }
        confirmLabel={data.status === "completed" ? "Discard" : "Cancel import"}
        danger
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(importId, {
            onSuccess: () => {
              setConfirming(false);
              if (data.status !== "completed") onClose();
            },
          });
        }}
      />
    </Stack>
  );
}
